<template>
  <div class="tw-relative tw-h-full" @click="selectPlan()" :style="`--primary-500: ${primaryValue}`">
    <base-card
      :class="['tw-cursor-pointer tw-transition-all tw-duration-200 tw-overflow-hidden tw-h-full tw-shadow-sm', { 'tw-shadow-md selected tw-scale-105': isSelected }]"
      :style="isSelected ? selectedCardStyles : ''"
    >
      <div class="tw-flex">
        <img
          :src="planImageUrl"
          :fallback="businessImageUrl"
          onerror="this.onerror=null;this.src=this.attributes.fallback.value"
          class="tw-w-40 tw-my-auto tw-mr-3 tw-rounded-md"
        />
        <div class="tw-flex tw-flex-col tw-py-4 tw-w-full">
          <div class="tw-text-gray-700 tw-text-lg tw-font-normal">{{ businessPlan.name }}</div>
          <p class="tw-text-xs description-text">{{ businessPlan.descriptionShort }}</p>
          <div class="tw-flex tw-mt-2">
            <Money-Format
              v-show="businessPlan.priceCents.toString().slice(-2) === '00'"
              class="tw-text-gray-700 tw-text-lg"
              :value="businessPlan.priceCents"
              :locale="locale"
              :currency-code="businessPlan.priceCurrency"
              :subunits-value="true"
              :hide-subunits="true"
            ></Money-Format>
            <Money-Format
              v-show="businessPlan.priceCents.toString().slice(-2) !== '00'"
              class="tw-text-gray-700 tw-text-lg"
              :value="businessPlan.priceCents"
              :locale="locale"
              :currency-code="businessPlan.priceCurrency"
              :subunits-value="true"
              :hide-subunits="false"
            ></Money-Format>

            <span class="tw-text-gray-500 tw-text-sm tw-ml-1">{{ cycleIntervalText }}</span>
            <div v-if="businessPlan.downPaymentCents > 0" class="tw-flex tw-ml-2">
              +
              <Money-Format
                v-show="businessPlan.downPaymentCents.toString().slice(-2) === '00'"
                class="tw-text-gray-500 tw-pb-0 tw-mb-0"
                :value="businessPlan.downPaymentCents"
                :locale="locale"
                :currency-code="businessPlan.priceCurrency"
                :subunits-value="true"
                :hide-subunits="true"
              ></Money-Format>
              <Money-Format
                v-show="businessPlan.downPaymentCents.toString().slice(-2) !== '00'"
                class="tw-text-gray-500 tw-pb-0 tw-mb-0"
                :value="businessPlan.downPaymentCents"
                :locale="locale"
                :currency-code="businessPlan.priceCurrency"
                :subunits-value="true"
                :hide-subunits="false"
              ></Money-Format>
              <span class="tw-text-gray-500 tw-text-sm tw-ml-1">/ down</span>
            </div>
          </div>
          <div class="tw-flex">
            <del>
              <Money-Format
                v-show="businessPlan.cycleRetailCostInCents.toString().slice(-2) === '00'"
                class="tw-text-gray-500 tw-text-sm tw-my-auto"
                :value="businessPlan.cycleRetailCostInCents"
                :locale="locale"
                :currency-code="businessPlan.priceCurrency"
                :subunits-value="true"
                :hide-subunits="true"
              ></Money-Format>
              <Money-Format
                v-show="businessPlan.cycleRetailCostInCents.toString().slice(-2) !== '00'"
                class="tw-text-gray-500 tw-text-sm tw-my-auto"
                :value="businessPlan.cycleRetailCostInCents"
                :locale="locale"
                :currency-code="businessPlan.priceCurrency"
                :subunits-value="true"
                :hide-subunits="false"
              ></Money-Format
            ></del>

            <div v-if="businessPlan.isCustomSavings" class="tw-text-green-700 tw-text-sm tw-ml-1">({{ businessPlan.savingsPercent }}%)</div>
            <div v-else class="tw-text-green-700 tw-text-sm tw-ml-1">({{ businessPlan.savingsTotalPercent }}%)</div>
          </div>
          <div class="tw-flex tw-my-2 tw-justify-start">
            <base-button class="tw-whitespace-nowrap" type="text" :style="`--primary-500: ${secondaryValue}; --primary-600: ${secondaryValue};`" @click="showDescriptionModal"
              >Plan Details</base-button
            >
          </div>
        </div>
      </div>

      <div class="tw-border-t tw-pt-2" v-if="showIncludesFooter">
        <div v-for="item in businessPlan.items" :key="`${item.businessItemCategoryId}-today`" class="tw-pt-2">
          <div class="tw-text-gray-600 tw-text-sm" v-if="item.initialQuantity + item.quantity">
            {{ item.initialQuantity + item.quantity }} {{ item.name }} credit{{ item.initialQuantity + item.quantity > 1 ? "s" : "" }} today!
          </div>
        </div>
        <div v-for="item in businessPlan.items" :key="`${item.businessItemCategoryId}-monthly`" class="tw-pt-2">
          <div class="tw-text-gray-600 tw-text-sm" v-if="item.quantity">
            {{ item.quantity }} {{ item.name }} credit{{ item.quantity > 1 ? "s" : "" }} each
            {{ businessPlan.cycleFrequency === 1 ? businessPlan.cycleInterval : businessPlan.cycleFrequency + " " + businessPlan.cycleInterval + "s" }}
          </div>
        </div>
        <div class="tw-pt-2">
          <div class="tw-text-gray-600 tw-text-sm">
            + Unlimited add-on credits at
            <span class="tw-text-green-700"> {{ businessPlan.savingsTotalPercent }}% off!</span>
          </div>
        </div>
      </div>
      <Details :businessPlan="businessPlan" ref="descriptionModal" />
    </base-card>
    <check-icon fixed-width :class="['check', { selected: isSelected }]" :style="selectedCheckStyles" />
  </div>
</template>

<script>
import Details from "@/views/business/business-member-signup/components/Details";
import MoneyFormat from "vue-money-format";
import chroma from "chroma-js";
import { CheckIcon } from "@/assets/svgs";

export default {
  name: "MemberLocationPlan",
  components: {
    Details,
    MoneyFormat,
    CheckIcon,
  },
  props: {
    businessId: String,
    primaryColor: String,
    secondaryColor: String,
    businessPlan: Object,
    businessName: String,
    showButton: Boolean,
    isSelected: Boolean,
  },
  computed: {
    savingsDescription() {
      return this.businessPlan.isAutoRenew === true ? "Annual" : "Total";
    },
    businessImageUrl() {
      return `/api/uploads/businesses/${this.businessId}/avatar/base-image`;
    },
    planImageUrl() {
      return `/api/uploads/businesses/${this.businessId}/plans/${this.businessPlan.businessPlanId}/avatar/base-image`;
    },
    term() {
      if (this.businessPlan.isAutoRenew === false) {
        if (this.businessPlan.cycleEndCount != null) {
          return this.businessPlan.cycleEndCount + " payments";
        } else {
          return this.businessPlan.cycleEndInterval == "year"
            ? this.businessPlan.cycleEndIntervalCount * 12 + " months"
            : this.businessPlan.cycleEndIntervalCount > 1
            ? this.businessPlan.cycleEndIntervalCount + " " + this.businessPlan.cycleEndInterval + "s"
            : this.businessPlan.cycleEndIntervalCount + " " + this.businessPlan.cycleEndInterval;
        }
      } else {
        return this.businessPlan.termInterval == "year"
          ? this.businessPlan.termLength * 12 + " months"
          : this.businessPlan.termLength > 1
          ? this.businessPlan.termLength + " " + this.businessPlan.termInterval + "s"
          : this.businessPlan.termLength + " " + this.businessPlan.termInterval;
      }
    },
    cycleIntervalText() {
      var cycleFrequencyText = this.businessPlan.cycleFrequency === 1 ? "/ " : "every " + this.businessPlan.cycleFrequency;
      var cycleIntervalText = this.businessPlan.cycleInterval === "month" ? "mo" : this.businessPlan.cycleInterval;

      return this.businessPlan.cycleFrequency === 1 ? cycleFrequencyText + " " + cycleIntervalText : cycleFrequencyText + " " + cycleIntervalText + "s";
    },
    calculatedRetailPrice() {
      return this.businessPlan.retailPriceCents + this.businessPlan.initialRetailPriceCents / this.businessPlan.termLength;
    },
    primaryValue() {
      return this.primaryColor ? this.primaryColor : "#ff0090";
    },
    secondaryValue() {
      return this.secondaryColor ? this.secondaryColor : "#404040";
    },
    locale() {
      return window.navigator.language;
    },
    selectedCardStyles() {
      const primaryColor = chroma(this.primaryColor ? this.primaryColor : "#ff0090");

      return {
        "border-color": primaryColor.hex(),
        "box-shadow": `0 0.5rem 1rem ${primaryColor.alpha(0.2).hex()} !important`,
      };
    },
    selectedCheckStyles() {
      const primaryColor = chroma(this.primaryColor ? this.primaryColor : "#ff0090");

      return {
        "background-color": primaryColor.hex(),
      };
    },
    showIncludesFooter() {
      return this.businessPlan.items.find((item) => item.initialQuantity + item.quantity);
    },
  },
  methods: {
    selectPlan() {
      this.$emit("selectPlanEvent", this.businessPlan.businessPlanId, !this.isSelected);
    },
    showDescriptionModal() {
      this.$refs.descriptionModal.show();
    },
    savingsValue(initialRetailPriceCents, retailPriceCents, priceCents, termInterval, termLength, cycleInterval, cycleFrequency) {
      let iterations;

      if (initialRetailPriceCents > 0) {
        const iterationSavings = this.calculatedRetailPrice - priceCents;
        return iterationSavings * termLength;
      } else {
        switch (cycleInterval) {
          case "day":
            iterations = 365 / cycleFrequency;
            break;
          case "week":
            iterations = 52 / cycleFrequency;
            break;
          case "month":
            iterations = 12 / cycleFrequency;
            break;
        }

        const iterationSavings = retailPriceCents - priceCents;
        const totalSavings = iterations * iterationSavings;

        return totalSavings;
      }
    },
    savingsPercent(initialRetailPriceCents, retailPriceCents, priceCents, termInterval, termLength, cycleInterval, cycleFrequency) {
      if (priceCents) {
        let iterations;

        if (initialRetailPriceCents > 0) {
          const totalCost = priceCents * termLength;
          const totalRetail = initialRetailPriceCents;
          const totalSavingsPercent = ((1 - totalCost / totalRetail) * 100).toFixed(0) + "%";

          return totalSavingsPercent;
        } else {
          switch (cycleInterval) {
            case "day":
              iterations = 365 / cycleFrequency;
              break;
            case "week":
              iterations = 52 / cycleFrequency;
              break;
            case "month":
              iterations = 12 / cycleFrequency;
              break;
          }

          const totalCost = iterations * priceCents;
          const totalRetail = retailPriceCents * iterations;
          const totalSavingsPercent = ((1 - totalCost / totalRetail) * 100).toFixed(0) + "%";

          return totalSavingsPercent;
        }
      } else return "0%";
    },
  },
};
</script>

<style lang="scss" scoped>
.check {
  position: absolute;
  top: -0.75rem;
  right: -0.75rem;
  color: white;
  height: 1.5rem;
  width: 1.5rem;
  padding: 0.375rem;
  background-color: red;
  border-radius: 100%;
  opacity: 0;
  transition: opacity;
  transition-duration: 250ms;

  &.selected {
    opacity: 1;
  }
}
.description-text.p {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

@media screen and (max-width: 768px) {
  .plan-image {
    max-width: 70px;
    height: auto !important;
  }
  .description-block {
    min-width: 200px;
  }
}
</style>
